import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { api } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { AsyncButton } from "./ui/async-button.js";
import { Textarea } from "./ui/textarea.js";
import { cn } from "../lib/utils.js";

const IST = "Asia/Kolkata";

export interface CorrectionRequest {
  id: string;
  date: string;
  requestedCheckIn:  string | null;
  requestedCheckOut: string | null;
  reason: string;
  status: string;
  origin: string;
  approverRole: string | null;
  /** Set once a request has been actioned; the list endpoint returns the whole row. */
  approverRemark?: string | null;
  employee: { id: string; firstName: string; lastName: string; employeeCode: string };
  attendanceRecord: { checkIn: string | null; checkOut: string | null } | null;
}

interface Props {
  requests:  CorrectionRequest[];
  onRefresh: () => void;
}

function fmtDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: IST });
}
function fmtTime(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: IST });
}

/** The employee's own words for why the correction was raised. Given its own
 * labelled row so it reads as part of the request, not as a caption. */
function ReasonRow({ reason }: { reason: string }) {
  return (
    <div className="flex items-baseline gap-2 border-t border-[var(--color-surface-200)] px-3 py-1.5">
      <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-[var(--color-surface-500)]">Reason</span>
      {/* Wraps instead of clamping: a one-word reason keeps the row one line
          high, a long one grows the box to fit rather than hiding text. */}
      <span className="min-w-0 flex-1 whitespace-pre-wrap break-words text-[13px] text-[var(--color-surface-800)]">
        {reason}
      </span>
    </div>
  );
}

/** One half of the before/after box: a label over "check in -> check out". */
function TimePair({ label, checkIn, checkOut, emphasis = false }: {
  label: string; checkIn: string; checkOut: string; emphasis?: boolean;
}) {
  return (
    <div className="flex min-w-0 items-baseline gap-2 px-3 py-1.5">
      <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-[var(--color-surface-500)]">{label}</span>
      <span className={cn("truncate text-[13px] font-medium", emphasis ? "text-[var(--color-brand-600)]" : "text-[var(--color-surface-800)]")}>
        {checkIn} <span className="text-[var(--color-surface-400)]">&rarr;</span> {checkOut}
      </span>
    </div>
  );
}

export function PendingAttendanceCorrectionsQueue({ requests, onRefresh }: Props) {
  const [remarks, setRemarks] = useState<Record<string, string>>({});
  const [err,     setErr]     = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  async function doApprove(id: string) {
    setErr("");
    try {
      const remark = (remarks[id] ?? "").trim();
      await api.patch(`/attendance/corrections/${id}/approve`, remark ? { approverRemark: remark } : {});
      onRefresh();
    } catch (e) {
      logError("PendingAttendanceCorrectionsQueue.doApprove", e);
      setErr("Failed to approve — try again");
      throw e;
    }
  }

  async function doReject(id: string) {
    const remark = (remarks[id] ?? "").trim();
    if (!remark) { setErr("Enter a remark for the decline"); throw new Error("validation"); }
    setErr("");
    try {
      await api.patch(`/attendance/corrections/${id}/reject`, { approverRemark: remark });
      onRefresh();
    } catch (e) {
      logError("PendingAttendanceCorrectionsQueue.doReject", e);
      setErr("Failed to decline — try again");
      throw e;
    }
  }

  if (!requests.length) {
    return (
      <div style={{ padding: "20px 0", color: "var(--color-surface-400)", fontSize: 13, textAlign: "center" }}>
        No pending attendance corrections
      </div>
    );
  }

  return (
    <div>
      {err && (
        <div style={{ background: "var(--color-error-bg)", color: "var(--color-error-fg)", padding: "8px 12px", borderRadius: 8, fontSize: 13, marginBottom: 10 }}>
          {err}
        </div>
      )}

      {requests.map((r) => {
        const expanded = expandedId === r.id;
        return (
          <div key={r.id} style={{
            background: "var(--color-surface-0)",
            border: "1px solid var(--color-surface-200)",
            borderRadius: 10, marginBottom: 8, overflow: "hidden",
          }}>
            <button
              onClick={() => setExpandedId(expanded ? null : r.id)}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, width: "100%", padding: "10px 14px", background: "none", border: "none", cursor: "pointer", textAlign: "left" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, overflow: "hidden" }}>
                {expanded ? <ChevronDown size={14} color="var(--color-surface-500)" style={{ flexShrink: 0 }} /> : <ChevronRight size={14} color="var(--color-surface-500)" style={{ flexShrink: 0 }} />}
                <span style={{ fontWeight: 600, fontSize: 14, color: "var(--color-surface-900)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.employee.firstName} {r.employee.lastName}
                </span>
                <span style={{ fontSize: 12, color: "var(--color-surface-400)", flexShrink: 0 }}>{r.employee.employeeCode}</span>
                {r.origin === "SYSTEM_BIOMETRIC" && (
                  <span style={{ fontSize: 11, fontWeight: 600, color: "var(--color-warning-fg)", background: "var(--color-warning-bg)", padding: "2px 8px", borderRadius: 999, flexShrink: 0 }}>
                    Auto-flagged
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12, color: "var(--color-surface-500)", flexShrink: 0 }}>{fmtDate(r.date)}</div>
            </button>

            {expanded && (
              <div className="border-t border-[var(--color-surface-100)] px-3.5 pb-3 pt-2.5">
                {/* Current and requested times sit side by side in one box, so the
                    before/after comparison reads across instead of down the card. */}
                <div className="mb-2 overflow-hidden rounded-lg border border-[var(--color-surface-200)] bg-[var(--color-surface-50)]">
                  <div className="grid grid-cols-2 divide-x divide-[var(--color-surface-200)]">
                    <TimePair
                      label={r.attendanceRecord ? "Current (Manual)" : "Current"}
                      checkIn={r.attendanceRecord ? fmtTime(r.attendanceRecord.checkIn) : "—"}
                      checkOut={r.attendanceRecord ? fmtTime(r.attendanceRecord.checkOut) : "—"}
                    />
                    <TimePair
                      label={r.origin === "SYSTEM_BIOMETRIC" ? "Proposed (Biometric)" : "Requested"}
                      checkIn={fmtTime(r.requestedCheckIn)}
                      checkOut={fmtTime(r.requestedCheckOut)}
                      emphasis
                    />
                  </div>
                  <ReasonRow reason={r.reason} />
                </div>

                {/* Remark and the decision buttons share one row to save height. */}
                <div className="flex items-start gap-2">
                  <Textarea
                    autoGrow
                    minRows={1}
                    maxRows={5}
                    size="sm"
                    aria-label="Approval remark"
                    placeholder="Add a remark (optional to approve, required to decline)…"
                    value={remarks[r.id] ?? ""}
                    onChange={(e) => setRemarks((prev) => ({ ...prev, [r.id]: e.target.value }))}
                    className="min-w-0 flex-1"
                  />
                  <AsyncButton size="sm" variant="success" onClick={() => doApprove(r.id)}>Approve</AsyncButton>
                  <AsyncButton size="sm" variant="destructive" onClick={() => doReject(r.id)}>Decline</AsyncButton>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
